import { cloneDeep, omit, uniqBy } from 'lodash';

import { extractCombineSteps } from '@/core/operation';
import {
  AddRelatedColumnOperation,
  BasePipeline,
  Cell,
  CellWithPipeline,
  CohortOperation,
  Exploration,
  ExplorationTab,
  FunnelOperation,
  JoinPipelineOperation,
  Metric,
  Model,
  QueryVariables,
  RelationAggregateOperation,
} from '@/explore/types';
import { flattenPipeline, generatePipelineId } from '@/explore/pipeline/utils';
import { getVariableDefinitions, getVariableKeysInPipeline } from '@/explore/utils';

import { getFinalState } from '@/explore/pipeline/state';
import {
  addCell,
  ensureUniqueVariableKey,
  generateCellId,
  getCellByPipelineIdOrThrow,
  getNextInsertPoint,
  isCellWithPipeline,
  isCellWithTitle,
  isReplicableCell,
  isSqlCell,
  isVariableCell,
  isVariableCellWithQueryOptions,
  removeConversationId,
  ReplicableCell,
  setCellPipeline,
} from '@/core/cell';
import {
  addExplorationCellsAt,
  getCellTabId,
  getExplorationCell,
  getExplorationCellsByTab,
  getExplorationTabs,
  getRootPipelineCell,
  setCellTabId,
  setExplorationCells,
  setExplorationTabs,
} from '@/core/exploration';
import { createPipelineWithParent, isCombinePipeline, isPipelineWithParent } from '@/core/pipeline';
import { isNil, unlessNil } from '@/lib/utils';
import { generateShortId } from '@/lib/utils/string';

// TODO: most of this should be moved to the core

export const prepareExplorationCellsForImport = (cells: Cell[]) => {
  const pipelineIdsToReplace = getPipelineIdsInCells(cells);
  const cellsWithNewPipelineIds = pipelineIdsToReplace.reduce(
    (acc, pipelineId) => replacePipelineIdInCells(acc, pipelineId, generatePipelineId()),
    cells,
  );

  return cellsWithNewPipelineIds.map((cell) => ({
    ...cell,
    id: generateCellId(),
    ...(isCellWithPipeline(cell) ? { pipeline: cell.pipeline } : {}),
    viewOptions: omit(cell.viewOptions, 'rowId'),
  }));
};

export const importExplorationCellsToExploration = (
  exploration: Exploration,
  cell: Cell,
  dependencies: Cell[],
  options?: {
    tabId?: string | null;
    index?: number;
  },
) => {
  const cellIds = exploration.view.cells.map((c) => c.id);
  const cellsWithNewPipelineIds = prepareExplorationCellsForImport(
    [cell].concat(dependencies.filter(({ id }) => !cellIds.includes(id))),
  ).map((cell) =>
    isVariableCell(cell) ? ensureUniqueVariableKey(cell, exploration.view.cells) : cell,
  );

  const updatedExploration = [...cellsWithNewPipelineIds].reduce(
    (explorationAcc, cellToMove) =>
      setCellTabId(explorationAcc, cellToMove.id, options?.tabId ?? null),
    exploration,
  );

  const [addedCell, ...addedCellDependencies] = cellsWithNewPipelineIds;

  return {
    exploration: addExplorationCellsAt(
      updatedExploration,
      cellsWithNewPipelineIds,
      options?.index ?? 0,
    ),
    cell: addedCell,
    dependencies: addedCellDependencies,
  };
};

export const duplicateExplorationCell = (
  exploration: Exploration,
  cellId: string,
): { exploration: Exploration; cell?: Cell } => {
  const cells = exploration.view.cells;
  const cell = getExplorationCell(exploration, cellId);

  if (cell === undefined) {
    return { exploration };
  }

  const cellCopy = isVariableCell(cell)
    ? duplicateCell(ensureUniqueVariableKey(cell, cells))
    : duplicateCell(cell);

  const insertPointId = getNextInsertPoint(exploration.view.cells, cellId);
  const tabId = getCellTabId(exploration, cellId) ?? null;

  return {
    exploration: setCellTabId(
      setExplorationCells(exploration, addCell(cells, cellCopy, insertPointId)),
      cellCopy.id,
      tabId,
    ),
    cell: cellCopy,
  };
};

export const createExplorationCellInstance = (
  exploration: Exploration,
  cellId: string,
): { exploration: Exploration; cell?: Cell } => {
  const cells = exploration.view.cells;
  const cell = getExplorationCell(exploration, cellId);

  if (cell === undefined || !isCellWithPipeline(cell)) {
    return { exploration };
  }

  const cellCopy = createCellInstance(cell);

  const insertPointId = getNextInsertPoint(exploration.view.cells, cellId);
  const tabId = getCellTabId(exploration, cellId) ?? null;

  return {
    exploration: setCellTabId(
      setExplorationCells(exploration, addCell(cells, cellCopy, insertPointId)),
      cellCopy.id,
      tabId,
    ),
    cell: cellCopy,
  };
};

export const getFieldsByCellId = (
  cellId: string,
  exploration: Exploration,
  models: Model[],
  variables: QueryVariables,
  metrics: Metric[],
) => {
  const cell = getExplorationCell(exploration, cellId);
  if (cell === undefined || !('pipeline' in cell)) {
    return [];
  }
  return getFinalState(cell.pipeline, {
    models,
    variables,
    metrics,
    exploration,
  }).fields;
};

const getReplicableExplorationCells = (exploration: Exploration): ReplicableCell[] =>
  exploration.view.cells.filter(isReplicableCell);

export const explorationHasReplicableCells = (exploration: Exploration) =>
  getReplicableExplorationCells(exploration).length > 0;

export const duplicateCell = (cell: Cell, rename: boolean = true): Cell => ({
  ...cloneDeep(removeConversationId(cell)),
  id: generateCellId(),
  ...(isCellWithTitle(cell) && !isNil(cell.title) && cell.title !== '' && rename
    ? { title: `Copy of ${cell.title}` }
    : {}),
  ...(isCellWithPipeline(cell)
    ? { pipeline: { ...cloneDeep(cell.pipeline), pipelineId: generatePipelineId() } }
    : {}),
});

export const createCellInstance = (cell: CellWithPipeline): Cell => ({
  ...omit(cloneDeep(removeConversationId(cell)), 'visualisations'),
  id: generateCellId(),
  kind: isSqlCell(cell) ? 'records' : cell.kind,
  title: `Instance of ${cell.title}`,
  pipeline: createPipelineWithParent({ parentId: cell.pipeline.pipelineId, operations: [] }),
  ...(unlessNil(cell.viewOptions, (opts) => ({
    viewOptions: omit(cloneDeep(opts), 'tableVisibility'),
  })) ?? {}),
});

export const duplicateExplorationTab = (
  exploration: Exploration,
  tabId: string,
): { exploration: Exploration; tab: ExplorationTab | undefined } => {
  const tabs = getExplorationTabs(exploration);
  if (tabs.length === 0) {
    return { exploration, tab: undefined };
  }

  const sourceTab = tabs.find((t) => t.tabId === tabId);
  if (sourceTab === undefined) {
    return { exploration, tab: undefined };
  }

  const sourceCells = getExplorationCellsByTab(exploration, tabId);

  // Replace all pipeline IDs in one coordinated pass so that cross-references between
  // cells within the tab (parentId, joinPipeline steps, cohort, funnel, addRelatedColumn,
  // relationAggregate, etc.) are updated to the new IDs before cell IDs are assigned.
  const pipelineIdsToReplace = getPipelineIdsInCells(sourceCells);
  const cellsWithUpdatedPipelineIds = pipelineIdsToReplace.reduce(
    (acc, pipelineId) => replacePipelineIdInCells(acc, pipelineId, generatePipelineId()),
    sourceCells,
  );

  const duplicatedCells = cellsWithUpdatedPipelineIds.map((cell) => ({
    ...cloneDeep(removeConversationId(cell)),
    id: generateCellId(),
  }));

  const newTab: ExplorationTab = {
    tabId: generateShortId(),
    label: `${sourceTab.label} (Copy)`,
    cellIds: duplicatedCells.map((cell) => cell.id),
  };

  const explorationWithCells = setExplorationCells(exploration, [
    ...exploration.view.cells,
    ...duplicatedCells,
  ]);

  const explorationWithTab = setExplorationTabs(explorationWithCells, [...tabs, newTab]);

  return {
    exploration: explorationWithTab,
    tab: newTab,
  };
};

export const cloneCell = (cell: Cell): Cell => ({
  ...cloneDeep(cell),
  id: generateCellId(),
  ...(isCellWithPipeline(cell)
    ? { pipeline: createPipelineWithParent({ parentId: cell.pipeline.pipelineId, operations: [] }) }
    : {}),
});

const replacePipelineIdInAddRelatedColumnOperation = (
  operation: AddRelatedColumnOperation,
  oldId: string,
  newId: string,
) =>
  'pipelineId' in operation.parameters && operation.parameters.pipelineId === oldId
    ? { ...operation, parameters: { ...operation.parameters, pipelineId: newId } }
    : operation;

const replacePipelineIdInJoinPipelineOperation = (
  operation: JoinPipelineOperation,
  oldId: string,
  newId: string,
): JoinPipelineOperation => {
  return {
    ...operation,
    parameters: {
      ...operation.parameters,
      steps: operation.parameters.steps.map((step) => {
        if (isPipelineWithParent(step.pipeline) && step.pipeline.parentId === oldId) {
          return { ...step, pipeline: { ...step.pipeline, parentId: newId } };
        }
        return step;
      }),
    },
  };
};

const replacePipelineIdInRelationAggregateOperation = (
  operation: RelationAggregateOperation,
  oldId: string,
  newId: string,
) =>
  'pipelineId' in operation.parameters && operation.parameters.pipelineId === oldId
    ? { ...operation, parameters: { ...operation.parameters, pipelineId: newId } }
    : operation;

const replacePipelineIdInCohortOperation = (
  operation: CohortOperation,
  oldId: string,
  newId: string,
) => ({
  ...operation,
  parameters: {
    ...operation.parameters,
    pipeline:
      isPipelineWithParent(operation.parameters.pipeline) &&
      operation.parameters.pipeline.parentId === oldId
        ? { ...operation.parameters.pipeline, parentId: newId }
        : operation.parameters.pipeline,
  },
});

const replacePipelineIdInFunnelOperation = (
  operation: FunnelOperation,
  oldId: string,
  newId: string,
) => ({
  ...operation,
  parameters: {
    ...operation.parameters,
    steps: operation.parameters.steps.map((step) =>
      isPipelineWithParent(step.pipeline) && step.pipeline.parentId === oldId
        ? { ...step, pipeline: { ...step.pipeline, parentId: newId } }
        : step,
    ),
  },
});

const replacePipelineIdInCells = (cells: Cell[], oldId: string, newId: string): Cell[] =>
  cells.map((cell) => replacePipelineIdInCell(cell, oldId, newId));

const replacePipelineIdInCell = (cell: Cell, oldId: string, newId: string): Cell => {
  if (isVariableCellWithQueryOptions(cell) && cell.definition.queryOptions.pipelineId === oldId) {
    return {
      ...cell,
      definition: {
        ...cell.definition,
        queryOptions: { ...cell.definition.queryOptions, pipelineId: newId },
      },
    };
  }

  if (!isCellWithPipeline(cell)) {
    return cell;
  }

  return {
    ...cell,
    pipeline: {
      ...cell.pipeline,
      pipelineId: cell.pipeline.pipelineId === oldId ? newId : cell.pipeline.pipelineId,
      ...(isPipelineWithParent(cell.pipeline) && cell.pipeline.parentId === oldId
        ? { parentId: newId }
        : {}),
      operations: cell.pipeline.operations.map((operation) => {
        switch (operation.operation) {
          case 'addRelatedColumn':
            return replacePipelineIdInAddRelatedColumnOperation(operation, oldId, newId);
          case 'joinPipeline':
            return replacePipelineIdInJoinPipelineOperation(operation, oldId, newId);
          case 'relationAggregate':
            return replacePipelineIdInRelationAggregateOperation(operation, oldId, newId);
          case 'cohort':
            return replacePipelineIdInCohortOperation(operation, oldId, newId);
          case 'funnel':
            return replacePipelineIdInFunnelOperation(operation, oldId, newId);
          default:
            return operation;
        }
      }),
    },
  };
};

const getPipelineReferencesByOperations = (operations: BasePipeline['operations']) => {
  const pipelineIds = new Set<string>();

  operations.forEach((operation) => {
    switch (operation.operation) {
      case 'addRelatedColumn':
        if ('pipelineId' in operation.parameters && operation.parameters.pipelineId !== undefined) {
          pipelineIds.add(operation.parameters.pipelineId);
        }
        break;
      case 'joinPipeline':
        extractCombineSteps(operation).forEach((step) => {
          if (isPipelineWithParent(step.pipeline) && step.pipeline.parentId !== undefined) {
            pipelineIds.add(step.pipeline.parentId);
          }
        });
        break;
      case 'relationAggregate':
        if ('pipelineId' in operation.parameters && operation.parameters.pipelineId !== undefined) {
          pipelineIds.add(operation.parameters.pipelineId);
        }
        break;
      case 'funnel':
        operation.parameters.steps.forEach((step) => {
          if (isPipelineWithParent(step.pipeline)) {
            pipelineIds.add(step.pipeline.parentId);
          }
        });
        break;
      case 'cohort':
        if (isPipelineWithParent(operation.parameters.pipeline)) {
          pipelineIds.add(operation.parameters.pipeline.parentId);
        }
    }
  });

  return Array.from(pipelineIds);
};

const getPipelineIdsInCells = (cells: Cell[]) =>
  cells.reduce<string[]>(
    (acc, cell) => (isCellWithPipeline(cell) ? [...acc, cell.pipeline.pipelineId] : acc),
    [],
  );

const extractCellWithDependencies = (
  cell: Cell,
  exploration: Exploration,
  visited: Set<string> = new Set(),
): { cell: Cell; dependencies: Cell[] } => {
  if (!isCellWithPipeline(cell)) {
    return { cell, dependencies: [] };
  }

  // Track visited cells to prevent infinite recursion in cyclic dependencies
  if (visited.has(cell.id)) {
    return { cell, dependencies: [] };
  }
  visited.add(cell.id);

  // If the pipeline is a join pipeline or has a parent (instance block), we do not flatten it
  const pipeline =
    isCombinePipeline(cell.pipeline) ||
    isPipelineWithParent(cell.pipeline) ||
    getRootPipelineCell(exploration, cell)?.kind === 'sql'
      ? cell.pipeline
      : flattenPipeline(cell.pipeline, exploration);

  const referencedPipelineIds = getPipelineReferencesByOperations(pipeline.operations);
  // If pipeline still references parent pipeline, include it in the dependencies
  if (isPipelineWithParent(pipeline) && !referencedPipelineIds.includes(pipeline.parentId)) {
    referencedPipelineIds.unshift(pipeline.parentId);
  }

  // Variables
  const variables = getVariableDefinitions(exploration);
  const usedVariableKeys = getVariableKeysInPipeline(pipeline, variables);

  const variableDependencies = usedVariableKeys.reduce<Cell[]>((cells, variableKey) => {
    const variableCell = exploration.view.cells
      .filter(isVariableCell)
      .find((cell) => cell.definition.key === variableKey);

    if (variableCell === undefined) {
      return cells;
    }

    if (
      isVariableCellWithQueryOptions(variableCell) &&
      variableCell.definition.queryOptions.pipelineId !== pipeline.pipelineId
    ) {
      referencedPipelineIds.push(variableCell.definition.queryOptions.pipelineId);
    }

    return [...cells, variableCell];
  }, []);

  const pipelineDependencies = referencedPipelineIds.reduce<Cell[]>((cells, pipelineId) => {
    const { cell, dependencies } = extractCellWithDependencies(
      getCellByPipelineIdOrThrow(exploration.view.cells, pipelineId),
      exploration,
      visited,
    );
    return [...cells, cell, ...dependencies];
  }, []);

  return {
    cell: setCellPipeline(cell, pipeline),
    // Filter out the cell itself to prevent cyclic dependencies
    dependencies: uniqBy(variableDependencies.concat(pipelineDependencies), 'id').filter(
      (dep) => dep.id !== cell.id,
    ),
  };
};

const extractVariableCellWithDependencies = (
  cell: Cell,
  exploration: Exploration,
): { cell: Cell; dependencies: Cell[] } => {
  if (isVariableCellWithQueryOptions(cell)) {
    const { cell: dependency, dependencies } = extractCellWithDependencies(
      getCellByPipelineIdOrThrow(exploration.view.cells, cell.definition.queryOptions.pipelineId),
      exploration,
    );
    return { cell, dependencies: [dependency, ...dependencies] };
  }

  return { cell, dependencies: [] };
};

export const extractCellFromExploration = (
  cellId: string,
  exploration: Exploration,
): { cell: Cell; dependencies: Cell[] } => {
  const cell = getExplorationCell(exploration, cellId);

  if (cell === undefined) {
    throw new Error(`Could not find cell with id ${cellId}`);
  }

  if (isCellWithPipeline(cell)) {
    return cloneDeep(extractCellWithDependencies(cell, exploration));
  }

  if (isVariableCell(cell)) {
    return cloneDeep(extractVariableCellWithDependencies(cell, exploration));
  }

  return { cell: cloneDeep(cell), dependencies: [] };
};
